// React
import { useEffect, useLayoutEffect, useState, useCallback, forwardRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useForm } from 'react-hook-form';
// ライブラリ
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
// コンポーネント
import SkillTagEditor from '../../../components/input/SkillTagEditor/SkillTagEditor';
// カスタムフック
import { useAuth } from '../../../context/AuthContext';
import { useEditStateStorage } from '../../../hooks/useEditStateStorage';
import { useSkillTags } from '../../../hooks/useSkillTags';
// ユーティリティ
import { validationSchema } from '../../../utils/validationUtils';
import { successMessages, errorMessages } from '../../../utils/messagesUtils';
import { menu } from '../../../constants';
// スタイル
import sty from './Main.module.css';

const Profile = ({
    years,
    months,
    setYear,
    setMonth,
    setDay,
    handleGetDays,
}) => {
    const navigate = useNavigate()
    // props
    Profile.propTypes = {
        years: PropTypes.array.isRequired,
        months: PropTypes.array.isRequired,
        setYear: PropTypes.func.isRequired,
        setMonth: PropTypes.func.isRequired,
        setDay: PropTypes.func.isRequired,
        handleGetDays: PropTypes.func.isRequired,
    };

    const defaultProfile = {
        "last_name": "",
        "first_name": "",
        "last_name_kana": "",
        "first_name_kana": "",
        "birth_year": "",
        "birth_month": "",
        "birth_day": "",
        "birth_date": "",
        "gender": null,
        "closest_line": "",
        "closest_station": "",
        "graduation_year": "",
        "graduation_month": "",
        "graduation_type": "",
        "school_name": "",
        "school_subject": "",
        "job_title": ""
    };

    const { setLoginUser } = useAuth();
    const [originalData, setOriginalData] = useState(null);
    const [selectedSkillTags, setSelectedSkillTags] = useState([]);
    const [birthDateValidation, setBirthDateValidation] = useState(true);
    const [tagCategories, setTagCategories] = useState({});

    const { transformSkillTagsData } = useSkillTags({
        selectedTags: selectedSkillTags,
        setSelectedTags: setSelectedSkillTags,
        tagCategories,
        mode: 'editor'
    });

    // フォームのstateを更新
    const { register, handleSubmit, formState: { errors }, setValue, getValues, trigger, watch, reset } = useForm({
        mode: 'onSubmit', // 保存時にバリデーションを実行
        reValidateMode: 'onSubmit', // 一度エラーが発生した後も、次の保存時にのみ再バリデーションを実行
        defaultValues: defaultProfile,
    });

    const watchAllFields = watch();
    const getFormData = useCallback(() => {
        return {
            ...watchAllFields,
            skillTags: selectedSkillTags
        };
    }, [watchAllFields, selectedSkillTags]);
    
    // カスタムフックの使用
    const resetFormWithSkillTags = useCallback((data) => {
        if (data && data.skillTags) {
            setSelectedSkillTags(Array.isArray(data.skillTags) ? data.skillTags : []);
        }
        reset(data);
    }, [reset]);

    const { checkForEditData, clearStoredData } = useEditStateStorage(
        'profile',           // ストレージキープレフィックス
        getFormData,        // フォームデータ取得関数
        resetFormWithSkillTags, // フォームリセット関数
        originalData,        // オリジナルデータ
    );
    
    const onSubmit = async (data) => {
        // サーバへ送信する前に、birthdateのチェック行う        
        const birthDateValidationResult = validationSchema.profile_birthdate.isValidFullDateString.validate(data.birth_year, data.birth_month, data.birth_day)
        setBirthDateValidation(birthDateValidationResult)
        if(!birthDateValidationResult)return;

        try {
            const submitData = {
                ...data,
                skillTags: selectedSkillTags
            };

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submitData),
                credentials: "include",
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                clearStoredData();
                setOriginalData(submitData);
                setSelectedSkillTags(selectedSkillTags);
                setLoginUser(`${data.last_name} ${data.first_name}`);
                Swal.fire({ icon: 'success', title: successMessages.operation.created });
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else if(response.status === 400) {
                    const error = new Error();
                    error.front_message = errorMessages.common.validation_error;
                    throw error;
                } else {
                    throw new Error();
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    };

    // 最寄駅カウント
    const [clickStationCount, setClickStationCount] = useState(0);

    const handlePlusClick = () => {
        setClickStationCount(clickStationCount + 1);
    };

    const handleMinusClick = () => {
        setClickStationCount(clickStationCount - 1);
        setValue("closest_line2", '');
        setValue("closest_station2", '');
    };

    // 卒業年月
    const [graduationYear, setGraduationYear] = useState(''); // 現在の年から10年引いた年を初期値として設定(30歳)
    const [graduationMonth, setGraduationMonth] = useState(''); // 初期値として1月を設定
    const [graduationType, setGraduationType] = useState(''); // 

    // 職種分類
    const [clickJobCount, setClickJobCount] = useState(0);

    const handlePlusJobClick = () => {
        setClickJobCount(clickJobCount + 1);
    };

    const handleMinusJobClick = () => {
        setClickJobCount(clickJobCount - 1);
        setValue("job_title2", '')
    };
    
    const fetchData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            })
            const result = await response.json()
            if(response.status === 200 && result.status === 'OK'){
                const data = result?.data
                if (Object.keys(data).length === 0) {
                    const defaultProfileWithSkills = {
                        ...defaultProfile,
                        skillTags: []
                    };
                    setOriginalData(defaultProfileWithSkills);
                    reset(defaultProfileWithSkills);
                    checkForEditData(defaultProfileWithSkills);
                } else {
                    Object.keys(data).forEach(key => {
                        setValue(key, data[key] || '')
                    });

                    // 生年月日
                    if (data.birth_year) setYear(data.birth_year);
                    if (data.birth_month) setMonth(data.birth_month);
                    if (data.birth_day) setDay(data.birth_day);
                    if (data.birth_year && data.birth_month) {
                        handleGetDays(data.birth_year, data.birth_month);
                    }

                    // スキルタグ
                    if (data.skillTags && Array.isArray(data.skillTags)) {
                        setSelectedSkillTags(data.skillTags);
                    }

                    const dataWithSkills = {
                        ...data,
                        skillTags: data.skillTags || []
                    };
                    setOriginalData(dataWithSkills);
                    reset(data);
                    checkForEditData(dataWithSkills);
                    
                    // 最寄駅
                    setClickStationCount(data.closest_line2 && data.closest_station2 ? 1 : 0);
                    // 職種分類
                    setClickJobCount(data.job_title2 ? 1 : 0);
                }
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else if(response.status === 404) {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch(e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    }

    const fetchSkillTagsData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/skill-tags?userType=engineer`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                setTagCategories(transformSkillTagsData(result.data));
            } else {
                if (response.status === 401) {
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            });
        }
    }

    // 初回マウント時にデータを取得
    useLayoutEffect(() => {
        fetchData();
        fetchSkillTagsData();
    }, []);
    
    // 編集中データ復元時に表示欄を更新する
    useEffect(() => {
        const values = getValues();
        if (values.closest_line2 || values.closest_station2) {
            setClickStationCount(1);
        }
        if (values.job_title2) {
            setClickJobCount(1);
        }
        // 復元されたスキルタグを設定
        if (values.skillTags && Array.isArray(values.skillTags)) {
            setSelectedSkillTags(values.skillTags);
        } else if (typeof values.skillTags === 'string' && values.skillTags) {
            // カンマ区切りの文字列として保存されている場合
            setSelectedSkillTags(values.skillTags.split(',').filter(tag => tag.trim()));
        }
    }, [originalData, reset, getValues, setClickStationCount, setClickJobCount]);

    // エンターキーでの送信を防ぐ
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className={sty['form']} data-testid="profile-form">
            <div className={sty['form-tab__content-inner']}>
                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__name-label-flex']}`}>
                        <div className={sty['form__label']}>氏名</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={sty['form__input-flex']}>
                            <div className={sty['form__input-outer']}>
                                <p className={sty['form__input-content']}>姓</p>
                                <input {...register('last_name', validationSchema.last_name)} type="text" placeholder="山田" autoComplete="off" />
                                {errors.last_name && <p className="form__validation-msg">{errors.last_name.message}</p>}
                            </div>
                            <div className={sty['form__input-outer']}>
                                <p className={sty['form__input-content']}>名</p>
                                <input {...register('first_name', validationSchema.first_name)} type="text" placeholder="太郎" autoComplete="off" />
                                {errors.first_name && <p className="form__validation-msg">{errors.first_name.message}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__name-label-flex']}`}>
                        <div className={sty['form__label']}>フリガナ</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={sty['form__input-flex']}>
                            <div className={sty['form__input-outer']}>
                                <p className={sty['form__input-content']}>セイ</p>
                                <input {...register('last_name_kana', validationSchema.last_name_kana)} type="text" placeholder="ヤマダ" />
                                {errors.last_name_kana && <p className="form__validation-msg">{errors.last_name_kana.message}</p>}
                            </div>
                            <div className={sty['form__input-outer']}>
                                <p className={sty['form__input-content']}>メイ</p>
                                <input {...register('first_name_kana', validationSchema.first_name_kana)} type="text" placeholder="タロウ" />
                                {errors.first_name_kana && <p className="form__validation-msg">{errors.first_name_kana.message}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>生年月日</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={sty['form__input-flex']}>
                            <div className={sty['form__input-outer-birth-date']}>
                                <div className={sty['form__input-validation']}>
                                    <input {...register('birth_year', validationSchema.profile_birthdate.isValidYearString)} type="text" placeholder="1990" defaultValue={originalData?.birth_year || ''} className={sty['birth-date-input']}/>
                                </div>
                                <span>年</span>
                            </div>

                            <div className={sty['form__input-outer-birth-date']}>
                                <div className={sty['form__input-validation']}>
                                    <input {...register('birth_month', validationSchema.profile_birthdate.isValidMonthString)} type="text" placeholder="01" defaultValue={originalData?.birth_month || ''} className={sty['birth-date-input']}/>
                                </div>
                                <span>月</span>
                            </div>
                            <div className={`${sty['form__input-outer-birth-date']}`}>
                                <div className={sty['form__input-validation']}>
                                    <input {...register('birth_day', validationSchema.profile_birthdate.isValidDayString)} type="text" placeholder="01" defaultValue={originalData?.birth_day || ''} className={sty['birth-date-input']}/>
                                </div>
                                <span>日</span>
                            </div>
                        </div>
                        <div className={sty['form__input-outer']}>
                            {errors.birth_year && <p className="form__validation-msg">{errors.birth_year.message}</p>}
                            {errors.birth_month && <p className="form__validation-msg">{errors.birth_month.message}</p>}
                            {errors.birth_day && <p className="form__validation-msg">{errors.birth_day.message}</p>}
                            {(!errors.birth_year && !errors.birth_month && !errors.birth_day) && !birthDateValidation && <p className="form__validation-msg">{validationSchema.profile_birthdate.isValidFullDateString.message}</p>}
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>性別</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={sty['form__input-flex']}>
                            <div className={sty['form__input-outer-radio']}>
                                <input {...register("gender")} type="radio" id="man" name="gender" value="1" 
                                        checked={watch("gender") === "1"} onChange={() => setValue("gender", "1")} />
                                <label htmlFor="man">男</label>
                            </div>
                            <div className={sty['form__input-outer-radio']}>
                                <input {...register("gender")} type="radio" id="woman" name="gender" value="2" 
                                        checked={watch("gender") === "2"} onChange={() => setValue("gender", "2")} />
                                <label htmlFor="woman">女</label>
                            </div>
                            <div className={sty['form__input-outer-radio']}>
                                <input {...register("gender")} type="radio" id="other" name="gender" value="0" 
                                        checked={watch("gender") === "0"} onChange={() => setValue("gender", "0")} />
                                <label htmlFor="other">無回答</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>最寄駅</div>
                        <div className={sty['required']}>必須</div>

                        <div className={sty['form__plus-minus-sp']}>
                            {clickStationCount > 0 ? (
                                <button type="button" className={sty['minus']} onClick={handleMinusClick}>-</button>
                            ) : (
                                <button type="button" className={sty['plus']} onClick={handlePlusClick}>+</button>
                            )}
                        </div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={`${sty['form__input-flex']} ${sty['form__input-flex-mb']} ${sty['form__input-closest_station']}`}>
                            <div className={sty['form__input-outer-station']}>
                                <div className={sty['form__input-validation']}>
                                    <input {...register('closest_line', validationSchema.closest_line)} type="text" placeholder="JR山手" />
                                    {errors.closest_line && <p className="form__validation-msg">{errors.closest_line.message}</p>}
                                </div>
                                <span className={sty['form__first-span']}>線</span>
                            </div>
                            <div className={sty['form__input-outer-station']}>
                                <div className={sty['form__input-validation']}>
                                    <input {...register('closest_station', validationSchema.closest_station)} type="text" placeholder="神田" />
                                    {errors.closest_station && <p className="form__validation-msg">{errors.closest_station.message}</p>}
                                </div>
                                <span>駅</span>
                            </div>
                        </div>

                        {/* クリック回数に応じて複製された要素 */}
                        {[...Array(clickStationCount)].map((_, index) => (
                            <div key={`station-${index}`} className={`${sty['form__input-flex']} ${sty['form__input-flex-mb']} ${sty['form__input-closest_station']}`}>
                                <div className={sty['form__input-outer-station']}>
                                    <div className={sty['form__input-validation']}>
                                        <input {...register('closest_line2', validationSchema.closest_line2)} type="text" placeholder="JR山手" />
                                        {errors.closest_line2 && <p className="form__validation-msg">{errors.closest_line2.message}</p>}
                                    </div>
                                    <span className={sty['form__first-span']}>線</span>
                                </div>
                                <div className={sty['form__input-outer-station']}>
                                    <div className={sty['form__input-validation']}>
                                        <input {...register('closest_station2', validationSchema.closest_station2)} type="text" placeholder="神田" />
                                        {errors.closest_station2 && <p className="form__validation-msg">{errors.closest_station2.message}</p>}
                                    </div>
                                    <span>駅</span>
                                </div>
                            </div>
                        ))}

                        <div className={sty['form__plus-minus-pc']}>
                            {clickStationCount > 0 ? (
                                <button type="button" className={sty['minus']} onClick={handleMinusClick}>-</button>
                            ) : (
                                <button type="button" className={sty['plus']} onClick={handlePlusClick}>+</button>
                            )}
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>最終学歴</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <div className={`${sty['form__input-flex']} ${sty['form__input-flex-mb']}`}>
                            <div className={sty['form__select-outer']}>
                                <select 
                                    aria-label="卒業年" 
                                    {...register("graduation_year", validationSchema.select)} 
                                    value={getValues("graduation_year") || graduationYear} 
                                    role="combobox" 
                                    onChange={(e) => {
                                        const selectedYear = e.target.value; 
                                        setValue("graduation_year",selectedYear);
                                        setGraduationYear(parseInt(selectedYear));
                                        trigger('graduation_year');
                                    }}
                                >
                                    <option hidden value="">選択</option>
                                    {years.map((y) => (
                                        <option key={y} value={y}>
                                            {y + '年'}
                                        </option>
                                    ))}
                                </select>
                                {errors.graduation_year && <p className="form__validation-msg">{errors.graduation_year.message}</p>}
                            </div>
                            <div className={sty['form__select-outer']}>
                                <select 
                                    aria-label="卒業月" 
                                    {...register("graduation_month", validationSchema.select)} 
                                    value={getValues("graduation_month") || graduationMonth} 
                                    role="combobox" 
                                    onChange={(e) => {
                                        const selectedMonth = e.target.value; 
                                        setValue("graduation_month",selectedMonth);
                                        setGraduationMonth(parseInt(selectedMonth));
                                        trigger('graduation_month');
                                    }}
                                >
                                    <option hidden value="">選択</option>
                                    {months.map((m) => (
                                        <option key={m} value={m}>
                                            {m + '月'}
                                        </option>
                                    ))}
                                </select>
                                {errors.graduation_month && <p className="form__validation-msg">{errors.graduation_month.message}</p>}
                            </div>
                            <div className={sty['form__select-outer']}>
                                <select 
                                    aria-label="卒業ステータス" 
                                    {...register("graduation_type", validationSchema.select)} 
                                    value={getValues("graduation_type") || graduationType} 
                                    role="combobox"  
                                    onChange={(e) => {
                                        const selectedType = e.target.value; 
                                        setValue("graduation_type",selectedType);
                                        setGraduationType(selectedType);
                                        trigger('graduation_type');
                                    }}
                                >
                                    <option hidden value="">選択</option>
                                    <option value="1">卒業</option>
                                    <option value="2">中退</option>
                                    <option value="3">在学中</option>
                                </select>
                                {errors.graduation_type && <p className="form__validation-msg">{errors.graduation_type.message}</p>}
                            </div>
                        </div>

                        <div className={sty['form__input-flex']}>
                            <div className={sty['form__input-outer']}>
                                <input {...register('school_name', validationSchema.school_name)} type="text" placeholder="4年制" />
                                {errors.school_name && <p className="form__validation-msg">{errors.school_name.message}</p>}
                            </div>
                            <div className={sty['form__input-outer']}>
                                <input {...register('school_subject', validationSchema.school_subject)} type="text" placeholder="大学" />
                                {errors.school_subject && <p className="form__validation-msg">{errors.school_subject.message}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>職種分類</div>
                        <div className={sty['required']}>必須</div>
                        <div className={sty['form__plus-minus-sp']}>
                            {clickJobCount > 0 ? (
                                <button type="button" className={sty['minus']} onClick={handleMinusJobClick}>-</button>
                            ) : (
                                <button type="button" className={sty['plus']} onClick={handlePlusJobClick}>+</button>
                            )}
                        </div>

                    </div>
                    <div className={sty['form__input']}>
                        <div className={`${sty['form__input-outer-job']} ${sty['form__input-flex-mb']}`}>
                            <input {...register('job_title', validationSchema.job_title)} type="text" placeholder="バックエンドエンジニア" />
                            {errors.job_title && <p className="form__validation-msg">{errors.job_title.message}</p>}
                        </div>

                        {/* クリック回数に応じて複製された要素 */}
                        {[...Array(clickJobCount)].map((_, index) => (
                            <div key={`job-${index}`} className={sty['form__input-outer-job']}>
                                <input {...register('job_title2', validationSchema.job_title2)} type="text" placeholder="バックエンドエンジニア" />
                                {errors.job_title2 && <p className="form__validation-msg">{errors.job_title2.message}</p>}
                            </div>
                        ))}

                        <div className={sty['form__plus-minus-pc']}>
                            {clickJobCount > 0 ? (
                                <button type="button" className={sty['minus']} onClick={handleMinusJobClick}>-</button>
                            ) : (
                                <button type="button" className={sty['plus']} onClick={handlePlusJobClick}>+</button>
                            )}
                        </div>
                    </div>
                </div>

                <div className={sty['form__flex']}>
                    <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                        <div className={sty['form__label']}>得意なスキル</div>
                        <div className={sty['required']}>必須</div>
                    </div>
                    <div className={sty['form__input']}>
                        <SkillTagEditor 
                            selectedTags={selectedSkillTags}
                            setSelectedTags={(newTags) => {
                                setSelectedSkillTags(newTags);
                                setValue('skillTags', newTags.join(','));
                            }}
                            tagCategories={tagCategories}
                        />
                        {/* スキルタグのバリデーション用*/}
                        <input
                            type="hidden"
                             {...register('skillTags', validationSchema.skillTags)}
                            value={selectedSkillTags.join(',')}
                        />
                        {errors.skillTags && <p className="form__validation-msg">{errors.skillTags.message}</p>}
                    </div>
                </div>
            </div >

            <div className="primary-btns">
                <button type="submit" className="primary-btn">保存</button>
            </div>
        </form >
    )
}

export default Profile;
